import { Box, useTheme } from '@mui/material'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

import Navbar from '@/components/navbar'
import useAppInit from '@/hooks/appInit'
import type { RootState } from '@/store'
import { useCreateCaseMutation } from '@/store/apiService'
import { setIsLoggingOut } from '@/store/appSlice'
import {
  RecipeGenerationStatus,
  setCurrentCaseId,
  setRecipeGenerationStatus,
} from '@/store/sessionSlice'
import { getCaseDetailsApi, getMetadataApi } from '@/utils/appApi'
import { NAVBAR_HEIGHT, RECIPE_TITLE } from '@/utils/constants'

interface CreateCaseResponse {
  id: string
  name: string
}

const AppLayout = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const { isAuthenticated } = useAppInit()

  const currentCaseId = useSelector(
    (state: RootState) => state.session.currentCaseId,
  )

  const [createNewCase] = useCreateCaseMutation()

  useEffect(() => {
    const fetchProfileAndOthers = async () => {
      try {
        await getMetadataApi()
      } catch (err: any) {
        if (err?.response?.data?.code === 1001) {
          navigate('/get-started/profile')
        }
      }
    }

    fetchProfileAndOthers()
  }, [isAuthenticated, currentCaseId])

  const useQuery = () => {
    return new URLSearchParams(useLocation().search)
  }
  const query = useQuery()
  const caseIdFromUrl = query.get('caseId')

  useEffect(() => {
    dispatch(setIsLoggingOut(false))
    if (caseIdFromUrl) {
      dispatch(setCurrentCaseId(caseIdFromUrl))
    }
  }, [dispatch, caseIdFromUrl])

  const extractProjectId = (pathname: string): string | null => {
    const match = pathname.match(/^\/projects\/([a-f0-9-]{36})$/)
    return match ? match[1] : null
  }

  useEffect(() => {
    const pathname = location.pathname
    const updatedCaseId = extractProjectId(pathname)

    const updateCaseId = async () => {
      if (updatedCaseId) {
        if (
          updatedCaseId === currentCaseId ||
          updatedCaseId === caseIdFromUrl
        ) {
          if (caseIdFromUrl !== updatedCaseId) {
            const newSearchParams = new URLSearchParams(location.search)
            newSearchParams.set('caseId', updatedCaseId)
            navigate({
              pathname: location.pathname,
              search: newSearchParams.toString(),
            })
          }
          return
        }

        try {
          const caseDetails = await getCaseDetailsApi(updatedCaseId)
          if (
            caseDetails?.role !== 'viewer' &&
            updatedCaseId !== currentCaseId
          ) {
            dispatch(setCurrentCaseId(updatedCaseId))
            // Update URL with caseId query param if not present or different
            if (caseIdFromUrl !== updatedCaseId) {
              const newSearchParams = new URLSearchParams(location.search)
              newSearchParams.set('caseId', updatedCaseId)
              navigate({
                pathname: location.pathname,
                search: newSearchParams.toString(),
              })
            }
          }
        } catch (error) {
          console.error('Failed to get case details:', error)
        }
      }
    }
    updateCaseId()
  }, [
    location.pathname,
    dispatch,
    navigate,
    currentCaseId,
    caseIdFromUrl,
    location.search,
  ])

  const isValidCaseId = !!currentCaseId

  useEffect(() => {
    if (!currentCaseId && caseIdFromUrl) {
      return
    }
    const pathname = location.pathname
    const recipePathToCheck = Object.keys(RECIPE_TITLE).find((key) =>
      pathname.includes(key),
    )

    const shouldCreateCase = !!recipePathToCheck && !isValidCaseId

    const initializeCase = async () => {
      if (shouldCreateCase) {
        const newCaseData = {
          id: uuidv4(),
          name: '--',
          case_type: 'auto',
        }

        try {
          const response = (await createNewCase(
            newCaseData,
          ).unwrap()) as CreateCaseResponse
          const newCaseId = response?.id || ''
          dispatch(setCurrentCaseId(newCaseId))

          // Update URL with newly created caseId
          const newSearchParams = new URLSearchParams(location.search)
          newSearchParams.set('caseId', newCaseId)
          navigate({
            pathname: location.pathname,
            search: newSearchParams.toString(),
          })
        } catch (error) {
          console.error('Failed to create new case:', error)
        }
      } else if (isValidCaseId) {
        // No longer setting default header as it's moved to path
      }
    }

    initializeCase()
  }, [
    location.pathname,
    isValidCaseId,
    isAuthenticated,
    currentCaseId,
    caseIdFromUrl,
    location.search,
    navigate,
    createNewCase,
  ])

  // Reset recipe generation status when route changes to avoid it from being stuck in GENERATING state Also Handle cursor on canvas
  useEffect(() => {
    dispatch(setRecipeGenerationStatus(RecipeGenerationStatus.DEFAULT))
  }, [dispatch, location])

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      bgcolor={theme.palette.background.default}
      color={theme.palette.text.primary}
    >
      <Navbar />
      <Box
        display="flex"
        flexGrow={1}
        height={`calc(100vh - ${NAVBAR_HEIGHT}px)`}
      >
        <Outlet />
      </Box>
    </Box>
  )
}

export default AppLayout
